import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import Sidebar from "../Dashboard/Sidebar";
import Topbar from "../Dashboard/Topbar";
import Loader from "../../components/Loader";
import { ToastContainer, toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import ConfirmModal from "../../components/ConfirmModal";
import SuccessModal from "../../components/SuccessModal";
import {
  paginateClinics,
  ClinicPaginationPayload,
  removeClinic,
} from "../../api/clinic";

const defaultPayload: ClinicPaginationPayload = {
  curPage: 1,
  perPage: 10,
  sortBy: "createdOn",
  direction: "desc",
  whereClause: [],
};

const ClinicList: React.FC = () => {
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [loading, setLoading] = useState(false);
  const [clinics, setClinics] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [total, setTotal] = useState(0);
  const [payload, setPayload] =
    useState<ClinicPaginationPayload>(defaultPayload);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [selectedId, setSelectedId] = useState<string>("");
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number } | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRefs = useRef<Record<string, HTMLButtonElement>>({});
  const lastFetchKeyRef = useRef<string>("");

  const totalPages = Math.max(1, Math.ceil(total / payload.perPage));

  const fetchClinics = async () => {
    setLoading(true);
    try {
      const filters: { key: string; value: string; operator: string }[] = [];

      if (searchTerm.trim()) {
        filters.push({
          key: "clinicName",
          value: searchTerm.trim(),
          operator: "=",
        });
      }

      const res = await paginateClinics({
        ...payload,
        whereClause: filters,
      });
      const data = res?.data?.data || res?.data || {};
      setClinics(
        Array.isArray(data.items) ? data.items : Array.isArray(data) ? data : []
      );
      setTotal(res.data.total || 0);
    } catch (err) {
      setClinics([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const key = `${payload.curPage}|${payload.perPage}|${payload.sortBy}|${payload.direction}|${searchTerm}`;
    if (lastFetchKeyRef.current === key) return;
    lastFetchKeyRef.current = key;
    fetchClinics();
  }, [payload.curPage, payload.perPage, payload.sortBy, payload.direction, searchTerm]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      // Check if click is outside the open menu and button
      const isClickOnButton = Object.values(buttonRefs.current).some(
        (btn) => btn && btn.contains(target)
      );
      const isClickOnMenu = target.closest('.dropdown-menu-container');
      
      if (!isClickOnButton && !isClickOnMenu && openMenuId) {
        setOpenMenuId(null);
        setDropdownPosition(null);
      }
    };

    if (openMenuId) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openMenuId]);

  const goToPage = (p: number) => {
    setPayload((prev) => ({
      ...prev,
      curPage: Math.min(Math.max(1, p), totalPages),
    }));
  };
  const changePerPage = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = parseInt(e.target.value, 10) || 10;
    setPayload((prev) => ({ ...prev, perPage: value, curPage: 1 }));
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    setPayload((prev) => ({ ...prev, curPage: 1 }));
  };

  const requestDelete = (id: string) => {
    setSelectedId(id);
    setShowConfirm(true);
  };

  const confirmDelete = async () => {
    if (!selectedId) return;
    setLoading(true);
    try {
      await removeClinic(selectedId);
      setShowConfirm(false);
      setShowSuccess(true);
      fetchClinics();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to delete clinic");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex h-screen bg-gray-100">
      <ToastContainer position="top-right" autoClose={3000} />
      {loading && <Loader />}

      <Sidebar
        isOpen={isSidebarOpen}
        toggle={() => setIsSidebarOpen(!isSidebarOpen)}
      />

      <div className="flex-1 flex flex-col">
        <Topbar toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />

        <main className="p-4 md:p-4 overflow-y-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="w-full max-w-6xl mx-auto"
          >
            <motion.div
              initial={{ scale: 0.98, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.4 }}
              className="w-full rounded-3xl shadow-2xl bg-white p-6 md:p-8"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl md:text-2xl font-bold text-gray-800">
                  Clinics
                </h2>
                <button
                  className="px-5 py-2 rounded-xl bg-blue-600 text-white font-medium hover:bg-blue-700 transition"
                  onClick={() => navigate("/clinics/create")}
                >
                  + New Clinic
                </button>
              </div>

              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">
                    Total: <span className="font-medium">{total}</span>
                  </span>
                 
                </div>
                <div className="flex items-center gap-2">
                <div className="w-full md:w-64">
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={handleSearchChange}
                    placeholder="Search by clinic name..."
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                   
                </div>
                <span className="text-sm text-gray-600">Rows:</span>
                  <select
                    className="border rounded-lg p-1 select-plain"
                    value={payload.perPage}
                    onChange={changePerPage}
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full border border-gray-200 rounded-lg overflow-hidden">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700">
                       Clinic Name
                      </th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700">
                        Address
                      </th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700">
                        Contact
                      </th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700">
                        Fees
                      </th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700">
                        Status
                      </th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {clinics.length === 0 ? (
                      <tr>
                        <td
                          className="px-4 py-6 text-center text-red-500"
                          colSpan={6}
                        >
                          No records found
                        </td>
                      </tr>
                    ) : (
                      clinics.map((c: any) => (
                        <tr key={c.id || c._id} className="border-t">
                          <td className="px-4 py-3 text-sm text-gray-800">
                            {c.name || c.clinicName}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {c.address || "—"}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {c.contact || "—"}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {c.fees != null ? c.fees : "—"}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <span
                              className={`px-2 py-1 rounded-full text-xs font-medium ${
                                c.isActive
                                  ? "bg-green-100 text-green-800"
                                  : "bg-red-100 text-red-800"
                              }`}
                            >
                              {c.isActive ? "Active" : "Inactive"}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div 
                              className="relative" 
                              ref={(el) => {
                                const clinicId = c.id || c._id || "";
                                if (openMenuId === clinicId) {
                                  (menuRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
                                }
                              }}
                            >
                              <button
                                ref={(el) => {
                                  if (el) {
                                    const clinicId = c.id || c._id || "";
                                    buttonRefs.current[clinicId] = el;
                                  }
                                }}
                                className="text-gray-600 hover:text-gray-900 focus:outline-none p-1 rounded-full hover:bg-gray-100"
                                onClick={(e) => {
                                  const clinicId = c.id || c._id || "";
                                  if (openMenuId === clinicId) {
                                    setOpenMenuId(null);
                                    setDropdownPosition(null);
                                  } else {
                                    const button = e.currentTarget;
                                    const rect = button.getBoundingClientRect();
                                    setDropdownPosition({
                                      top: rect.bottom + window.scrollY + 4,
                                      left: rect.right + 4,
                                    });
                                    setOpenMenuId(clinicId);
                                  }
                                }}
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="h-5 w-5"
                                  fill="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <circle cx="12" cy="5" r="2" />
                                  <circle cx="12" cy="12" r="2" />
                                  <circle cx="12" cy="19" r="2" />
                                </svg>
                              </button>
                              {openMenuId === (c.id || c._id) && dropdownPosition && createPortal(
                                <div 
                                  className="dropdown-menu-container fixed w-32 bg-white rounded-lg shadow-lg border border-gray-200 z-[9999] overflow-hidden"
                                  style={{
                                    top: `${dropdownPosition.top}px`,
                                    left: `${dropdownPosition.left}px`,
                                  }}
                                >
                                  <button
                                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                                    onClick={() => {
                                      navigate(`/clinics/create/${c.id || c._id}`);
                                      setOpenMenuId(null);
                                      setDropdownPosition(null);
                                    }}
                                  >
                                    Edit
                                  </button>
                                  <div className="border-t border-gray-200"></div>
                                  <button
                                    className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100 transition-colors"
                                    onClick={() => {
                                      requestDelete(c.id || c._id);
                                      setOpenMenuId(null);
                                      setDropdownPosition(null);
                                    }}
                                  >
                                    Delete
                                  </button>
                                </div>,
                                document.body
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              <hr />
              <div className="flex items-center justify-between mt-6">
                <div className="text-sm text-gray-600">
                  Page {payload.curPage} of {totalPages}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    className="px-3 py-1.5 border rounded-lg text-sm hover:bg-gray-50"
                    onClick={() => goToPage(1)}
                    disabled={payload.curPage === 1}
                  >
                    « First
                  </button>
                  <button
                    className="px-3 py-1.5 border rounded-lg text-sm hover:bg-gray-50"
                    onClick={() => goToPage(payload.curPage - 1)}
                    disabled={payload.curPage === 1}
                  >
                    ‹ Prev
                  </button>
                  <button
                    className="px-3 py-1.5 border rounded-lg text-sm hover:bg-gray-50"
                    onClick={() => goToPage(payload.curPage + 1)}
                    disabled={payload.curPage >= totalPages}
                  >
                    Next ›
                  </button>
                  <button
                    className="px-3 py-1.5 border rounded-lg text-sm hover:bg-gray-50"
                    onClick={() => goToPage(totalPages)}
                    disabled={payload.curPage >= totalPages}
                  >
                    Last »
                  </button>
                </div>
              </div>

              <ConfirmModal
                isOpen={showConfirm}
                title="Delete Clinic"
                message="Are you sure you want to delete this clinic?"
                confirmText="Yes, Delete"
                cancelText="Cancel"
                onConfirm={confirmDelete}
                onCancel={() => setShowConfirm(false)}
                loading={loading}
              />

              <SuccessModal
                isOpen={showSuccess}
                title="Clinic Deleted"
                message="The clinic has been deleted successfully."
                onClose={() => setShowSuccess(false)}
              />
            </motion.div>
          </motion.div>
        </main>
      </div>
    </div>
  );
};

export default ClinicList;
